/**
 * Interceptador específico para React Error Overlay (Create React App)
 * 
 * Este módulo intercepta e filtra erros específicos do Firebase
 * antes que cheguem ao React Error Overlay, evitando modais
 * desnecessários para erros conhecidos que são falsos alarmes.
 */

// Controle de logs via variáveis de ambiente
const SHOW_REACT_OVERLAY_LOGS = import.meta.env.VITE_SHOW_REACT_OVERLAY_LOGS === 'true';
const DISABLE_ERROR_MASKING = import.meta.env.VITE_DISABLE_ERROR_MASKING === 'true';

let isInterceptorActive = false;

/**
 * Configura o interceptador do React Error Overlay
 */
export const setupReactErrorOverlayInterceptor = () => {
  if (import.meta.env.MODE !== 'development' || isInterceptorActive || DISABLE_ERROR_MASKING) {
    if (DISABLE_ERROR_MASKING && SHOW_REACT_OVERLAY_LOGS) {
      console.log("🚨 [REACT OVERLAY INTERCEPTOR] MASCARAMENTO DESABILITADO - Todos os erros serão exibidos no overlay");
    }
    return;
  }

  // Aguarda o carregamento do React Error Overlay
  const checkForErrorOverlay = () => {
    try {
      // Intercepta a função que constrói errors para o overlay
      if (window.__react_error_overlay_reporter__) {
        const reporter = window.__react_error_overlay_reporter__;
        
        if (reporter.captureConsoleIntegration) {
          const originalCaptureConsoleIntegration = reporter.captureConsoleIntegration;
          
          reporter.captureConsoleIntegration = function(error) {
            // Filtra erros conhecidos do Firebase
            if (error && 
                (error.message?.includes('Missing or insufficient permissions') ||
                 error.name === 'FirebaseError')) {
              
              if (SHOW_REACT_OVERLAY_LOGS) {
                console.log("🔕 [REACT OVERLAY INTERCEPTOR] Erro Firebase filtrado:", {
                  message: error.message,
                  name: error.name,
                  timestamp: new Date().toISOString()
                });
              }
              
              return; // Não envia para o overlay
            }
            
            // Para outros erros, usa o comportamento original
            return originalCaptureConsoleIntegration.call(this, error);
          };
        }
      }
      
      // Intercepta erros não tratados que podem chegar ao overlay
      const originalUnhandledRejection = window.onunhandledrejection;
      window.onunhandledrejection = function(event) {
        if (event.reason && 
            (event.reason.message?.includes('Missing or insufficient permissions') ||
             event.reason.code === 'permission-denied')) {
          
          if (SHOW_REACT_OVERLAY_LOGS) {
            console.log("🔕 [REACT OVERLAY INTERCEPTOR] Promise rejection filtrada:", {
              reason: event.reason.message,
              code: event.reason.code,
              timestamp: new Date().toISOString()
            });
          }
          
          event.preventDefault();
          return;
        }
        
        // Para outras rejeições, usa o handler original
        if (originalUnhandledRejection) {
          return originalUnhandledRejection.call(this, event);
        }
      };
      
      isInterceptorActive = true;
      if (SHOW_REACT_OVERLAY_LOGS) {
        console.log("🔧 [REACT OVERLAY INTERCEPTOR] Interceptador ativo");
      }
      
    } catch (error) {
      if (SHOW_REACT_OVERLAY_LOGS) {
        console.log("ℹ️ [REACT OVERLAY INTERCEPTOR] Não foi possível interceptar o overlay:", error.message);
      }
    }
  };

  // Tenta configurar imediatamente e também após um delay
  checkForErrorOverlay();
  
  // Aguarda um pouco para garantir que o overlay foi carregado
  setTimeout(checkForErrorOverlay, 1000);
  setTimeout(checkForErrorOverlay, 3000);
};

// Auto-executa em desenvolvimento
if (import.meta.env.MODE === 'development' && !DISABLE_ERROR_MASKING) {
  setupReactErrorOverlayInterceptor();
}
